import type { Habit, HabitCompletion, HabitSlot } from '../types';
import { DEFAULT_PRAYER_CONFIG } from './dayCycle';

/**
 * ⭐ Permanent interval ids for labelled habits (phase 3b-1, owner 2026-10-09).
 *
 * Before: a completion pointed at its interval by LABEL (`slot: 'Snack'`). Renaming an
 * interval orphaned its completions (they still counted, so a day could read 6/5, and
 * could no longer be undone), and two intervals sharing a name could never both be done.
 * Now each interval is `{ id, label }` and a completion records `slotId` (plus the label
 * as it was then, for history and older builds).
 *
 * THE RULES
 *  · The LABELS (`intervalLabels`) still decide whether a habit is labelled and how many
 *    intervals it has — older builds only know them — and `slots` supplies the ids.
 *  · A habit with labels but no ids gets DETERMINISTIC ids (`i1…iN`, or the prayer keys
 *    `fajr…isha` for the five prayers), so two devices adding them at once agree.
 *  · A completion with a `slotId` belongs to that interval or to none (a removed interval
 *    is history). One without — an older record — is matched by label ONLY when exactly
 *    one current interval has that label. Nothing is ever guessed.
 */

/** The five prayers as slots — ids are the prayer keys, labels the display names. */
export const PRAYER_SLOTS: readonly HabitSlot[] = DEFAULT_PRAYER_CONFIG.map(c => ({ id: c.key, label: c.name }));

/** A fresh id for an interval added from now on (unique within its habit). */
export function newSlotId(taken: ReadonlySet<string> = new Set()): string {
  for (;;) {
    const id = `s-${Math.random().toString(36).slice(2, 10)}`;
    if (!taken.has(id)) return id;
  }
}

/** Positional id `i<n>` — or a free variant if a habit already uses it. */
function positionalId(i: number, taken: ReadonlySet<string>): string {
  let id = `i${i + 1}`;
  for (let k = 2; taken.has(id); k++) id = `i${i + 1}-${k}`;
  return id;
}

/**
 * Ids for labels that never had them: the prayer keys (`fajr…isha`, by position) for the
 * prayer habit — or for the five prayer names in order — else i1…iN. A prayer habit keeps
 * its own spellings ("fajer", "Duher"…): the prayer is identified by the id, not the name.
 */
export function legacySlots(labels: readonly string[], opts: { prayers?: boolean } = {}): HabitSlot[] {
  const prayerNames = labels.length === PRAYER_SLOTS.length && labels.every((l, i) => l === PRAYER_SLOTS[i].label);
  if (labels.length === PRAYER_SLOTS.length && (opts.prayers || prayerNames)) {
    return PRAYER_SLOTS.map((s, i) => ({ id: s.id, label: labels[i] }));
  }
  const taken = new Set<string>();
  return labels.map((label, i) => { const id = positionalId(i, taken); taken.add(id); return { id, label }; });
}

/**
 * The slots for a set of labels, keeping existing ids. Labels equal to the slots' → the
 * slots as they are. Otherwise an OLDER build changed the labels (it only knows labels):
 * ids stay with their positions, new positions get fresh positional ids.
 */
export function reconcileSlots(slots: readonly HabitSlot[] | undefined, labels: readonly string[] | undefined, opts: { prayers?: boolean } = {}): HabitSlot[] | null {
  if (!labels || labels.length === 0) return null;
  if (!slots || slots.length === 0) return legacySlots(labels, opts);
  if (slots.length === labels.length && slots.every((s, i) => s.label === labels[i])) return [...slots];
  const taken = new Set(slots.slice(0, labels.length).map(s => s.id));
  return labels.map((label, i) => {
    if (slots[i]) return { id: slots[i].id, label };
    const id = positionalId(i, taken); taken.add(id); return { id, label };
  });
}

/** A habit's named intervals with their ids, in order — or null for an unlabelled habit. */
export function slotsOf(h: Pick<Habit, 'slots' | 'intervalLabels'> & { system?: Habit['system'] }): HabitSlot[] | null {
  // The labels decide (older builds write only them); `slots` lends the ids.
  if (h.intervalLabels && h.intervalLabels.length) return reconcileSlots(h.slots, h.intervalLabels, { prayers: h.system === 'prayers' });
  return null;
}

/**
 * ⭐ The intervals a habit had ON a date (owner, 2026-10-18): an earlier set from its
 * `slotHistory` when the date is before that set's `until`, else its current intervals.
 * A habit that never changed has no history, so this is exactly `slotsOf`.
 */
export function slotsOn(h: Pick<Habit, 'slots' | 'intervalLabels' | 'slotHistory'> & { system?: Habit['system'] }, date: string): HabitSlot[] | null {
  const past = historyOn(h, date);
  if (past) return past.slots.length ? past.slots : null;
  return slotsOf(h);
}

/** The `slotHistory` entry that covers a date — null when the date uses the current intervals. */
export function historyOn(h: Pick<Habit, 'slotHistory'>, date: string): NonNullable<Habit['slotHistory']>[number] | null {
  return [...(h.slotHistory ?? [])].sort((a, b) => a.until.localeCompare(b.until)).find(e => date < e.until) ?? null;
}

/** Which current interval a completion filled — or null (removed interval, or an ambiguous old label). */
export function slotOfCompletion(slots: readonly HabitSlot[], c: Pick<HabitCompletion, 'slot' | 'slotId'>): HabitSlot | null {
  if (c.slotId) return slots.find(s => s.id === c.slotId) ?? null;
  if (c.slot == null) return null;
  const byLabel = slots.filter(s => s.label === c.slot);
  return byLabel.length === 1 ? byLabel[0] : null;
}

/**
 * The interval a caller means: its id, or (older callers and stored routine steps pass a
 * label) the ONE interval with that label. Ambiguous or unknown → null.
 */
export function resolveSlot(slots: readonly HabitSlot[], ref: string): HabitSlot | null {
  const byId = slots.find(s => s.id === ref);
  if (byId) return byId;
  const byLabel = slots.filter(s => s.label === ref);
  return byLabel.length === 1 ? byLabel[0] : null;
}

/** Older completions that can't be placed because two intervals share their label — "Needs review". */
export function ambiguousCompletions(h: Habit): number {
  const slots = slotsOf(h);
  if (!slots) return 0;
  return (h.completions ?? []).filter(c => !c.bonus && !c.slotId && c.slot != null && slots.filter(s => s.label === c.slot).length > 1).length;
}

/**
 * Give a habit permanent interval ids (idempotent): `slots` set from the labels, the
 * `intervalLabels` mirror kept in step, and every older completion that can be placed
 * UNAMBIGUOUSLY gets its `slotId`. Returns the same object when nothing changes.
 */
export function withSlotIds(h: Habit): Habit {
  const slots = slotsOf(h);
  if (!slots) {
    // Labels removed (possibly by an older build): no intervals — drop stale ids.
    return h.slots ? { ...h, slots: undefined } : h;
  }
  let changed = !h.slots || h.slots.length !== slots.length || h.slots.some((s, i) => s.id !== slots[i].id || s.label !== slots[i].label);
  const completions = (h.completions ?? []).map(c => {
    if (c.bonus || c.slotId || c.slot == null) return c;
    const s = slotOfCompletion(slots, c);
    if (!s) return c;
    changed = true;
    return { ...c, slotId: s.id };
  });
  if (!changed) return h;
  return { ...h, slots, intervalLabels: slots.map(s => s.label), completions };
}

/** Does this habit carry the five prayer slots — ids `fajr…isha` in order? (Its own spellings are fine.) */
export function hasPrayerSlots(h: Pick<Habit, 'slots' | 'intervalLabels'> & { system?: Habit['system'] }): boolean {
  const slots = slotsOf(h);
  return !!slots && slots.length === PRAYER_SLOTS.length && slots.every((s, i) => s.id === PRAYER_SLOTS[i].id);
}

/**
 * The patch that makes a habit the PRAYER habit: ids `fajr…isha` by POSITION — keeping the
 * habit's own five spellings if it has five, else the standard names — and each past
 * completion moved with its position (interval 1 → fajr …) so nothing already done stops
 * counting. Completions of other positions are left as they are (history).
 */
export function toPrayerSlots(h: Habit): Pick<Habit, 'slots' | 'intervalLabels' | 'completions'> {
  const before = slotsOf(h) ?? [];
  const keep = before.length === PRAYER_SLOTS.length;
  const slots = PRAYER_SLOTS.map((s, i) => ({ id: s.id, label: keep ? before[i].label : s.label }));
  const byOldId = new Map(before.map((s, i) => [s.id, slots[i]]));
  const completions = (h.completions ?? []).map(c => {
    if (c.bonus) return c;
    const old = slotOfCompletion(before, c);
    const to = old ? byOldId.get(old.id) : undefined;
    return to && (c.slotId !== to.id || c.slot !== to.label) ? { ...c, slotId: to.id, slot: to.label } : c;
  });
  return { slots, intervalLabels: slots.map(s => s.label), completions };
}

/** `resolveSlot` against a habit's current slots (null for an unlabelled habit). */
export function resolveHabitSlot(h: Pick<Habit, 'slots' | 'intervalLabels'>, ref: string): HabitSlot | null {
  const slots = slotsOf(h);
  return slots ? resolveSlot(slots, ref) : null;
}
